import { produce } from "immer";
import React from "react";
import { apiPost } from "../api";

const useUnnamedComponent = (url) => {
  const [fetching, setFetching] = React.useState(false);
  const [resources, setResources] = React.useState([]);
  const [resourcesMeta, setResourcesMeta] = React.useState({});
  const [filterState, setFilterState] = React.useState({ query: {} });
  const [dateFilterState, setDateFilterState] = React.useState(() => {
    const now = new Date();
    return {
      dateQuery: {
        created_at: [new Date(now.getFullYear(), now.getMonth(), 1), now],
      },
    };
  });
  const [fetchable, setFetchable] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const perpage = 20;
  const isInitialMount = React.useRef(true);

  const filterCount = React.useMemo(() => {
    let counter = 0;
    for (const field in filterState.query) {
      if (Array.isArray(filterState.query[field])) {
        counter += filterState.query[field].length;
      } else {
        counter++;
      }
    }
    return counter;
  }, [filterState]);
  const clearable = React.useMemo(
    () => fetchable || filterCount !== 0,
    [fetchable, filterCount],
  );

  const fetchData = React.useCallback(async () => {
    setFetching(true);
    const { query } = filterState;
    const { dateQuery } = dateFilterState;
    const { parsedResponse, statusCode } = await apiPost(url, {
      body: {
        query: {
          ...query,
          ...dateQuery,
        },
        page: currentPage,
        perpage: perpage,
      },
    });
    if (statusCode === 200) {
      setResources(parsedResponse.data);
      setResourcesMeta(parsedResponse.meta);
      setFetchable(false);
      setFetching(false);
      // setFilterShown(false);
    }
  }, [filterState, dateFilterState, currentPage, url]);

  const handleChangeFilter = React.useCallback((evt) => {
    const { name, value, checked } = evt.target;
    setFilterState(
      produce((draft) => {
        if (checked) {
          if (Array.isArray(draft.query[name])) {
            draft.query[name].push(value);
          } else if (draft.query[name]) {
            draft.query[name] = [draft.query[name], value];
          } else {
            draft.query[name] = value;
          }
        } else {
          if (Array.isArray(draft.query[name])) {
            const idx = draft.query[name].indexOf(value);
            if (idx !== -1) {
              draft.query[name].splice(idx, 1);
            }
          } else if (draft.query[name]) {
            delete draft.query[name];
          }
        }
      }),
    );
    setFetchable(true);
  }, []);
  const handleClearFilter = React.useCallback(() => {
    setFilterState(
      produce((draft) => {
        draft.query = {};
      }),
    );
    setCurrentPage(1);
    fetchData();
  }, [fetchData]);
  const handleApplyFilter = React.useCallback(() => {
    setCurrentPage(1);
    fetchData();
  }, [fetchData]);
  const handlePageChange = React.useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);
  const inputParams = React.useCallback(
    (name, type, value = null) => ({
      name,
      ...(type === "checkbox"
        ? {
            checked: Array.isArray(filterState.query[name])
              ? filterState.query[name].indexOf(value) !== -1
              : filterState.query[name] === value,
            value: value,
          }
        : {
            value: filterState.query[name],
          }),
      type,
      onChange: handleChangeFilter,
    }),
    [filterState, handleChangeFilter],
  );

  const dateFilterRef = React.useRef();
  dateFilterRef.current = dateFilterState;
  const datePickerParams = React.useCallback(
    (name, idx) => ({
      dateFormat: "dd/MM/yyyy",
      selected: dateFilterRef.current.dateQuery[name][idx],
      onChange: (value) => {
        setDateFilterState(
          produce((draft) => {
            draft.dateQuery[name][idx] = value;
          }),
        );
      },
    }),
    [],
  );

  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchData();
    }
  }, []);

  React.useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [currentPage]);

  return {
    clearable,
    currentPage,
    dateFilterState,
    datePickerParams,
    fetchData,
    fetchable,
    fetching,
    filterCount,
    handleApplyFilter,
    handleClearFilter,
    handlePageChange,
    inputParams,
    perpage,
    resources,
    resourcesMeta,
  };
};
export default useUnnamedComponent;
